/**
 * Staff-specific product-group incentive rules (employee portal + monthly incentive).
 * Sale amounts use branch group-wise import totals (same as Monthly Sale Report import precalc).
 */

export const EMPLOYEE_SPECIAL_GROUP_INCENTIVE_PLANS = [
  {
    employeeId: "74",
    ratePct: 1,
    /** Branch-wide Item Group sales (not filtered by Employee Name on each line). */
    useBranchGroupSales: true,
    groups: [
      "AMARON",
      "APOLO",
      "CEAT",
      "TVS TYRE",
      "POWERZONE",
      "EXIDE",
    ],
  },
];

/** Alternate spellings seen in import Item Group / precalc keys. */
export const SPECIAL_GROUP_MATCH_ALIASES = {
  APOLO: ["APOLO", "APOLLO"],
  "TVS TYRE": ["TVS TYRE", "TVS TYRES", "TVSTYRE"],
  POWERZONE: ["POWERZONE", "POWER ZONE"],
  AMARON: ["AMARON"],
  CEAT: ["CEAT"],
  EXIDE: ["EXIDE"],
};

export function normalizeProductGroupKey(name) {
  return String(name || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function getSpecialGroupIncentivePlan(employeeId) {
  const eid = String(employeeId ?? "").trim();
  if (!eid) return null;
  return (
    EMPLOYEE_SPECIAL_GROUP_INCENTIVE_PLANS.find((p) => String(p.employeeId).trim() === eid) ?? null
  );
}

export function matchKeysForPlanGroup(groupId) {
  const canonical = String(groupId || "").trim();
  if (!canonical) return [];
  const aliases = SPECIAL_GROUP_MATCH_ALIASES[canonical] || [canonical];
  const keys = new Set();
  for (const a of aliases) {
    const k = normalizeProductGroupKey(a);
    if (k) keys.add(k);
  }
  keys.add(normalizeProductGroupKey(canonical));
  return [...keys];
}

export function matchPlanProductGroup(rawGroup, planGroups) {
  const key = normalizeProductGroupKey(rawGroup);
  if (!key || !planGroups?.length) return null;
  for (const g of planGroups) {
    for (const mk of matchKeysForPlanGroup(g)) {
      if (key === mk || key.includes(mk) || mk.includes(key)) return g;
    }
  }
  return null;
}

/**
 * Sum branch group-wise sale for one configured plan group label.
 * @param {{ key: string, sale: number }[]} groupSaleRows
 */
export function sumBranchSaleForPlanGroup(groupId, groupSaleRows) {
  const matchKeys = matchKeysForPlanGroup(groupId);
  if (!matchKeys.length) return 0;
  let total = 0;
  for (const row of groupSaleRows || []) {
    const label = String(row?.key ?? row?.label ?? "").trim();
    const sale = Number(row?.sale) || 0;
    if (!label || sale <= 0) continue;
    const nk = normalizeProductGroupKey(label);
    if (matchKeys.some((mk) => nk === mk || nk.includes(mk) || mk.includes(nk))) {
      total += sale;
    }
  }
  return total;
}

/** Item Group / profit centre text candidates on one import line. */
export function importRowProductGroupCandidates(row) {
  const out = [];
  const push = (v) => {
    const s = String(v ?? "").trim();
    if (s) out.push(s);
  };
  push(row?.itemGroup);
  push(row?.itemgroup);
  push(row?.["Item Group"]);
  push(row?.profitCentre);
  push(row?.profitCentreName);
  push(row?.brandName);
  push(row?.alias);
  return out;
}

export function matchPlanProductGroupFromRow(row, planGroups) {
  for (const raw of importRowProductGroupCandidates(row)) {
    const hit = matchPlanProductGroup(raw, planGroups);
    if (hit) return hit;
  }
  return null;
}

/** Display names on import rows for a configured special-plan employee. */
export function resolveEmployeeNamesForPlan(planEmployeeId, employees = [], staff = []) {
  const eid = String(planEmployeeId ?? "").trim();
  if (!eid) return [];
  const names = new Set();
  for (const e of employees) {
    if (String(e?.employeeId ?? "").trim() === eid) {
      const n = String(e?.employeeName ?? e?.name ?? "").trim();
      if (n) names.add(n);
    }
  }
  for (const row of staff || []) {
    if (String(row?.employeeId ?? "").trim() === eid) {
      const n = String(row?.name ?? "").trim();
      if (n) names.add(n);
    }
  }
  return [...names];
}
